/**
 * =============================================================================
 * SPEQ55 RFP Pro - Organization Management API Client
 * =============================================================================
 *
 * API client functions for organization management operations.
 *
 * @module api/organizations
 * @author SPEQ55
 */

import api from './client.js';

/**
 * Get all organizations with optional filters.
 *
 * @param {Object} [filters]
 * @param {boolean} [filters.is_active] - Filter by active status
 * @param {string} [filters.search] - Search by name/description
 * @param {boolean} [filters.includeStats] - Include user counts
 * @param {number} [filters.limit] - Max results (1-100)
 * @param {number} [filters.offset] - Pagination offset
 * @returns {Promise<Object>} { success, organizations, total }
 */
export async function getOrganizations(filters = {}) {
  const params = {};
  if (filters.is_active !== undefined) params.is_active = filters.is_active;
  if (filters.search) params.search = filters.search;
  if (filters.includeStats) params.includeStats = filters.includeStats;
  if (filters.limit) params.limit = filters.limit;
  if (filters.offset !== undefined) params.offset = filters.offset;

  const response = await api.get('/organizations', { params });
  return response.data;
}

/**
 * Get a single organization by ID.
 *
 * @param {string} orgId - Organization UUID
 * @returns {Promise<Object>} { success, organization }
 */
export async function getOrganizationById(orgId) {
  const response = await api.get(`/organizations/${orgId}`);
  return response.data;
}

/**
 * Create a new organization.
 *
 * @param {Object} payload
 * @param {string} payload.name - Organization name (2-255 chars, unique)
 * @param {string} [payload.description] - Optional description (max 2000 chars)
 * @returns {Promise<Object>} { success, message, organization }
 */
export async function createOrganization({ name, description }) {
  const response = await api.post('/organizations', { name, description });
  return response.data;
}
